import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  BookOpen,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  FileText
} from 'lucide-react';
import PassageReader from '../components/PassageReader';
import MathText from '../components/MathText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentQuizTake = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassagePanel, setShowPassagePanel] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchQuiz(token);
  }, [assignmentId, navigate]);

  const fetchQuiz = async (token) => {
    try {
      const response = await axios.get(`${API}/student/quiz/${assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuiz(response.data);
    } catch (error) {
      console.error('Error fetching quiz:', error);
      const message = error.response?.data?.detail || 'Failed to load quiz';
      toast.error(message);
      if (error.response?.status === 400) {
        // Already submitted
        navigate('/student/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionIndex, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }

    // Check if all questions answered
    const unanswered = quiz.questions.filter((_, i) => !answers[i]);
    if (unanswered.length > 0) {
      toast.warning(`Please answer all questions. ${unanswered.length} remaining.`);
      return;
    }

    setSubmitting(true);

    try {
      const submission = {
        assignment_id: quiz.assignment_id,
        answers: Object.entries(answers).map(([index, answer]) => ({
          question_index: parseInt(index),
          answer: answer
        }))
      };

      const response = await axios.post(`${API}/student/submit`, submission, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Quiz submitted!');
      navigate(`/student/results/${response.data.id}`);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = quiz ? ((Object.keys(answers).length / quiz.questions.length) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="bg-white border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Skeleton className="h-8 w-40" />
          </div>
        </nav>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-4 w-full mb-8" />
          <Skeleton className="h-64 w-full" />
        </main>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-serif text-xl font-medium text-primary mb-2">Quiz not found</h2>
          <Button onClick={() => navigate('/student/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/student/dashboard')}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-serif text-lg font-medium text-primary">{quiz.title}</h1>
                <p className="text-xs text-muted-foreground">{quiz.subject}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {quiz.show_passage && quiz.passage_text && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => setShowPassagePanel(!showPassagePanel)}
                  data-testid="toggle-passage-btn"
                >
                  {showPassagePanel ? (
                    <>
                      <EyeOff className="w-4 h-4" />
                      Hide Passage
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      View Passage
                    </>
                  )}
                </Button>
              )}
              <div className="text-sm text-muted-foreground">
                Question {currentQuestion + 1} of {quiz.questions.length}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Progress */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Progress value={progress} className="h-2" />
        <p className="text-xs text-muted-foreground mt-2 text-right">
          {Object.keys(answers).length} of {quiz.questions.length} answered
        </p>
      </div>

      {/* Passage Panel */}
      <AnimatePresence>
        {showPassagePanel && quiz.show_passage && quiz.passage_text && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-4"
          >
            <Card className="border-border bg-secondary/30">
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  Reading Passage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[260px] pr-4">
                  <PassageReader text={quiz.passage_text} />
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Question */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <motion.div
          key={currentQuestion}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="border-border shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-lg font-semibold text-primary">
                  {currentQuestion + 1}
                </span>
                <span className="text-xs font-medium px-3 py-1 bg-secondary rounded-full text-muted-foreground capitalize">
                  {question.question_type.replace('_', ' ')}
                </span>
              </div>
              <CardTitle className="font-serif text-xl leading-relaxed">
                <MathText>{question.question}</MathText>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {question.image_url && (
                <img
                  src={question.image_url}
                  alt="Question diagram"
                  className="mb-4 max-h-64 rounded-lg border border-border"
                />
              )}
              {/* Multiple Choice */}
              {question.question_type === 'multiple_choice' && question.options && (
                <RadioGroup
                  value={answers[currentQuestion] || ''}
                  onValueChange={(value) => handleAnswer(currentQuestion, value)}
                  className="space-y-3"
                >
                  {question.options.map((option, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        answers[currentQuestion] === option
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40'
                      }`}
                      onClick={() => handleAnswer(currentQuestion, option)}
                    >
                      <RadioGroupItem value={option} id={`option-${index}`} />
                      <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer text-base">
                        <span className="font-medium mr-2">{String.fromCharCode(65 + index)}.</span>
                        <MathText>{option}</MathText>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {/* True/False */}
              {question.question_type === 'true_false' && (
                <RadioGroup
                  value={answers[currentQuestion] || ''}
                  onValueChange={(value) => handleAnswer(currentQuestion, value)}
                  className="flex gap-4"
                >
                  {['True', 'False'].map((option) => (
                    <div
                      key={option}
                      className={`flex-1 flex items-center justify-center gap-3 p-6 rounded-xl border-2 cursor-pointer transition-all ${
                        answers[currentQuestion]?.toLowerCase() === option.toLowerCase()
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/40'
                      }`}
                      onClick={() => handleAnswer(currentQuestion, option)}
                    >
                      <RadioGroupItem value={option} id={`option-${option}`} />
                      <Label htmlFor={`option-${option}`} className="cursor-pointer text-lg font-medium">
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}

              {/* Fill in the Blank */}
              {question.question_type === 'fill_in_blank' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Your Answer</Label>
                  <Input
                    placeholder="Type your answer..."
                    value={answers[currentQuestion] || ''}
                    onChange={(e) => handleAnswer(currentQuestion, e.target.value)}
                    className="h-12 text-base"
                    data-testid="fill-in-answer"
                  />
                </div>
              )}

              {/* Numeric Fill in the Blank */}
              {question.question_type === 'numeric_fill_in_blank' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Your Answer</Label>
                  <Input
                    placeholder="Type a number, fraction (3/4), decimal (0.75) or percentage (75%)"
                    value={answers[currentQuestion] || ''}
                    onChange={(e) => handleAnswer(currentQuestion, e.target.value)}
                    className="h-12 text-base"
                    data-testid="numeric-fill-in-answer"
                    inputMode="text"
                  />
                  <p className="text-xs text-muted-foreground">
                    Equivalent forms are accepted (e.g., <code>1/2</code> = <code>0.5</code> = <code>50%</code>).
                  </p>
                </div>
              )}

              {/* Short Answer */}
              {question.question_type === 'short_answer' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Your Answer</Label>
                  <Textarea
                    placeholder="Write your answer..."
                    value={answers[currentQuestion] || ''}
                    onChange={(e) => handleAnswer(currentQuestion, e.target.value)}
                    className="min-h-[150px] text-base"
                    data-testid="short-answer"
                  />
                  <p className="text-xs text-muted-foreground">
                    Short answers will be reviewed by your teacher
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className="gap-2"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Button>

          {currentQuestion < quiz.questions.length - 1 ? (
            <Button
              onClick={() => setCurrentQuestion(prev => prev + 1)}
              className="gap-2 bg-primary hover:bg-primary/90"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="gap-2 bg-accent hover:bg-accent/90"
              data-testid="submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Submit Quiz
                </>
              )}
            </Button>
          )}
        </div>

        {/* Question Navigator */}
        <div className="mt-8 p-4 bg-secondary/30 rounded-xl">
          <p className="text-sm font-medium text-muted-foreground mb-3">Question Navigator</p>
          <div className="flex flex-wrap gap-2">
            {quiz.questions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestion(index)}
                className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                  index === currentQuestion
                    ? 'bg-primary text-primary-foreground'
                    : answers[index]
                    ? 'bg-primary/20 text-primary'
                    : 'bg-white border border-border text-muted-foreground hover:border-primary/40'
                }`}
                data-testid={`nav-q-${index}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentQuizTake;
